import { type LogLevel, type LogEventOptions, type ILogger } from '@types';
import { paintToast } from './paintToast';

function getLoggerFn (level: LogLevel, l: ILogger): NonNullable<ILogger['info']> {
  switch (level) {
  case 'info':    return l.info;
  case 'warning': return l.warning;
  case 'error':   return l.error;
  case 'success': return l.success ?? l.info;
  }
}

// Fans every log out to all transports. Owns toast painting so a toast shows
// exactly once no matter how many transports are active.
export class ParallelLoggerService implements ILogger {
  constructor (private readonly transports: ILogger[]) {}

  private async dispatch (
    level: LogLevel,
    message: string,
    options?: LogEventOptions,
  ): Promise<void> {
    const { showToast = false, toastOptions = {} } = options ?? {};

    if (showToast) {
      paintToast(level, message, toastOptions);
    }

    const forwarded: LogEventOptions = {
      context: {},
      ...options,
      showToast: false,
    };

    await Promise.all(
      this.transports.map(async (t) => {
        try {
          await getLoggerFn(level, t)(message, forwarded);
        } catch (err) {
          console.warn(`[Logger] Transport failed to send ${level} log:`, err);
        }
      }),
    );
  }

  public info = async (message: string, options?: LogEventOptions): Promise<void> => {
    await this.dispatch('info', message, options);
  };

  public warning = async (message: string, options?: LogEventOptions): Promise<void> => {
    await this.dispatch('warning', message, options);
  };

  public error = async (message: string, options?: LogEventOptions): Promise<void> => {
    await this.dispatch('error', message, options);
  };

  public success = async (message: string, options?: LogEventOptions): Promise<void> => {
    await this.dispatch('success', message, options);
  };
}
